import {
  ActionIcon,
  Box,
  Button,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import { FormValuesType } from '../../UI/dynamicUI/types';
import CurrencyInput from '../../common/CurrencyInput';
import { camelCaseOrSnakeCaseToWords } from '../../data-capture/merge-review/ReviewHelperFunctions';
import { FieldTypeMetadata } from '../../data-capture/types';
import classes from '../../data-capture/merge-review/MergeReviewPage.module.css';

type FormFieldProp = {
  form: UseFormReturnType<FormValuesType>;
  fieldName: string;
  entityMetadata: FieldTypeMetadata;
  disabled?: boolean;
  highlightNumber?: number;
  parentFieldName?: string;
  label?: string;
};

export const FormField = ({
  form,
  fieldName,
  entityMetadata,
  disabled = false,
  highlightNumber = undefined,
  parentFieldName = undefined,
  label = undefined,
}: FormFieldProp) => {
  try {
    const fieldType = entityMetadata.type;
    const fieldLabel = label || camelCaseOrSnakeCaseToWords(fieldName);
    const rightValue = highlightNumber ? <HighlightNumber number={highlightNumber} /> : null;

    const formFieldName = parentFieldName ? `${parentFieldName}.${fieldName}` : fieldName;
    const inputProps = form.getInputProps(formFieldName);
    if (fieldType === 'SELECT' || fieldType === 'BOOLEAN') {
      const options = [
        ...entityMetadata.options.map(option => ({
          value: option.value.toString(),
          label: option.label.toString(),
        })),
      ];

      return (
        <Select
          label={fieldLabel}
          required={!entityMetadata.isOptional}
          data={options}
          searchable={options.length > 10}
          disabled={disabled}
          rightSection={rightValue}
          clearable
          key={form.key(formFieldName)}
          {...inputProps}
          value={inputProps.value}
        />
      );
    }

    if (fieldType === 'MONEY') {
      const currencySymbol = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: entityMetadata.currency,
        maximumSignificantDigits: 1,
      }).formatToParts(0)[0].value;

      return (
        <Box>
          <CurrencyInput
            required={!entityMetadata.isOptional}
            key={form.key(formFieldName)}
            label={fieldLabel}
            disabled={disabled}
            leftSection={currencySymbol}
            {...inputProps}
          />
        </Box>
      );
    }

    if (fieldType === 'DATE') {
      return (
        <DateInput
          required={!entityMetadata.isOptional}
          clearable
          label={fieldLabel}
          disabled={disabled}
          rightSection={rightValue}
          placeholder="Click to select a date"
          valueFormat="D MMMM YYYY"
          {...inputProps}
          key={form.key(formFieldName)}
        />
      );
    }

    if (fieldType === 'PERCENTAGE') {
      const percentageWithRightValue = (
        <Box>
          <Text>%</Text>
          {rightValue}
        </Box>
      );
      return (
        <TextInput
          required={!entityMetadata.isOptional}
          key={form.key(formFieldName)}
          label={fieldLabel}
          type="number"
          rightSection={percentageWithRightValue}
          disabled={disabled}
          {...inputProps}
        />
      );
    }

    if (fieldType === 'LIST') {
      const renderListItem = () => {
        return (
          <Box>
            <Text mb={'md'} c={'gray.7'} size={'sm'} fw={500}>
              {fieldLabel}
            </Text>
            <Stack>
              {inputProps.defaultValue?.map((_: unknown, index: number) => {
                const listInputProps = form.getInputProps(`${formFieldName}.${index}`);
                return (
                  <Group key={index}>
                    <ActionIcon
                      variant="transparent"
                      radius="xl"
                      aria-label="Settings"
                      color={'black'}
                      mr={'md'}
                    >
                      <IconTrash onClick={() => form.removeListItem(formFieldName, index)} />
                    </ActionIcon>
                    <Stack>
                      <TextInput
                        required={!entityMetadata.isOptional}
                        flex={1}
                        key={form.key(`${formFieldName}.${index}`)}
                        placeholder=""
                        disabled={disabled}
                        rightSection={rightValue}
                        {...listInputProps}
                      />
                    </Stack>
                  </Group>
                );
              })}
            </Stack>
          </Box>
        );
      };

      return (
        <Stack>
          {renderListItem()}
          <Button
            onClick={() => {
              form.insertListItem(formFieldName, '');
            }}
          >
            Add
          </Button>
        </Stack>
      );
    }

    if (fieldType === 'TEXT') {
      return (
        <Textarea
          required={!entityMetadata.isOptional}
          resize="vertical"
          key={form.key(formFieldName)}
          label={fieldLabel}
          placeholder=""
          disabled={disabled}
          minRows={3}
          autosize
          {...inputProps}
        />
      );
    }

    return (
      <TextInput
        required={!entityMetadata.isOptional}
        key={form.key(formFieldName)}
        label={fieldLabel}
        placeholder=""
        disabled={disabled}
        rightSection={rightValue}
        {...inputProps}
      />
    );
  } catch (e) {
    throw new Error(`field name ${fieldName} caused an error: ${e}`);
  }
};

type HighlightNumberProps = {
  number: number;
};

const HighlightNumber = ({ number }: HighlightNumberProps) => {
  return (
    <Box className={classes.highlightNumberCircle}>
      <Text className={classes.highlightNumber}>{number}</Text>
    </Box>
  );
};
